/**
 * Obsidian × Ostrovsky prelaunch teaser (templates/page.ostrovsky-teaser.json).
 * Loaded once, deferred, by sections/ostrovsky-teaser-hero.liquid.
 *
 * Klaviyo owns the signup form: its markup, validation, submission and
 * success step. Nothing here renders a success state of its own. This file:
 *   - notices whether the embed rendered, and says so honestly if it didn't
 *   - after Klaviyo has replaced the form with its next step, tidies the
 *     surrounding copy and announces that step to screen readers
 *   - shows the "You're on the list" panel only when Klaviyo's double
 *     opt-in success redirect lands back here with ?ostrovsky_confirmed=1
 *   - scrolls to and focuses the form for [data-ot-jump] links
 *   - plays background loops only when motion is allowed, with a pause control
 *   - preserves custom funnel events in dataLayer without loading a pixel.
 *     custom-events.js publishes to Shopify (ARCHITECTURE.md §9). No email addresses are included.
 *
 * Campaign parameters (utm_*) are left on the URL untouched: Klaviyo's
 * "Store UTM parameters in shoppers' profile upon consent" form setting
 * reads them from there.
 */
(function () {
  'use strict';

  var SIGNUP_ID = 'ostrovsky-signup';
  var CONFIRMED_PARAM = 'ostrovsky_confirmed';
  var CAMPAIGN_KEY = 'ostrovsky_teaser_campaign';
  var CAMPAIGN_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  // Long enough for a slow Instagram in-app browser, short enough that a
  // visitor behind a content blocker isn't left staring at a loading box.
  var LOAD_TIMEOUT_MS = 12000;

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch (e) {
    params = { get: function () { return null; }, delete: function () {}, toString: function () { return ''; } };
  }

  /* ---- Campaign attribution ------------------------------------------
     First touch within the session, so a reload or an in-page jump that
     loses the query string still reports where the visitor came from. */
  function readCampaign() {
    var fresh = {};
    var found = false;
    CAMPAIGN_PARAMS.forEach(function (key) {
      var value = params.get(key);
      if (value) {
        fresh[key] = value.trim().slice(0, 100);
        found = true;
      }
    });

    try {
      var stored = JSON.parse(window.sessionStorage.getItem(CAMPAIGN_KEY) || 'null');
      if (stored) return stored;
      if (found) window.sessionStorage.setItem(CAMPAIGN_KEY, JSON.stringify(fresh));
    } catch (e) {
      /* Storage blocked (private mode): fall back to this pageview's params. */
    }
    return fresh;
  }

  var campaign = readCampaign();

  function track(name, payload) {
    window.dataLayer = window.dataLayer || [];
    var data = { event: name, teaser_page: 'ostrovsky' };
    var key;
    for (key in campaign) {
      if (Object.prototype.hasOwnProperty.call(campaign, key)) data[key] = campaign[key];
    }
    for (key in payload || {}) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) data[key] = payload[key];
    }
    window.dataLayer.push(data);
  }

  /* ---- Signup ------------------------------------------------------- */

  function initSignup() {
    var root = document.getElementById(SIGNUP_ID);
    if (!root) return;

    var formId = root.getAttribute('data-form-id');
    var embed = root.querySelector('[data-ot-klaviyo]');
    var status = root.querySelector('[data-ot-status]');
    var announcer = root.querySelector('[data-ot-announce]');
    var consentId = root.getAttribute('data-consent-id');

    showConfirmedIfReturning(root);

    if (!embed || !formId) return;

    var ready = false;
    var submitted = false;
    var settled = false;
    var settleTimer = null;

    function emailInput() {
      return embed.querySelector('input[type="email"], input[name="email"], input[autocomplete="email"]');
    }

    // Tie the consent copy to the field so it is read with the label.
    // Re-run on every render: Klaviyo rebuilds nodes on validation.
    function linkConsent() {
      var input = emailInput();
      if (!input || !consentId) return;
      var ids = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      if (ids.indexOf(consentId) === -1) {
        ids.push(consentId);
        input.setAttribute('aria-describedby', ids.join(' '));
      }
    }

    function markReady() {
      if (!ready) {
        ready = true;
        root.classList.remove('is-unavailable');
        if (status) status.textContent = '';
        track('ostrovsky_signup_form_ready', {});
      }
      linkConsent();
    }

    // Klaviyo fires `submit` for the conversion action, but the page only
    // reacts once Klaviyo has actually swapped the email field for its
    // next step. A rejected submission keeps the field, so nothing here
    // changes and Klaviyo's own error stays in charge.
    function settleAfterSubmit() {
      if (!submitted || settled) return;
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(function () {
        if (emailInput()) return;
        settled = true;
        root.classList.add('is-submitted');

        var message = (embed.innerText || embed.textContent || '').replace(/\s+/g, ' ').trim();
        if (announcer && message) announcer.textContent = message.slice(0, 400);

        // The button that had focus has just been removed from the DOM.
        embed.setAttribute('tabindex', '-1');
        if (!document.activeElement || document.activeElement === document.body || !root.contains(document.activeElement)) {
          embed.focus({ preventScroll: true });
        }
      }, 250);
    }

    if ('MutationObserver' in window) {
      new MutationObserver(function () {
        if (embed.children.length) markReady();
        settleAfterSubmit();
      }).observe(embed, { childList: true, subtree: true });
    }
    if (embed.children.length) markReady();

    window.addEventListener('klaviyoForms', function (event) {
      var detail = event.detail || {};
      if (detail.formId !== formId) return;

      if (detail.type === 'embedOpen') markReady();

      if (detail.type === 'submit') {
        submitted = true;
        track('ostrovsky_signup_submit', { klaviyo_form_id: formId });
        settleAfterSubmit();
      }
    });

    window.setTimeout(function () {
      if (ready || embed.children.length) return;
      root.classList.add('is-unavailable');
      showUnavailable(root, status);
      track('ostrovsky_signup_form_unavailable', { klaviyo_loaded: !!(window.klaviyo || window._learnq) });
    }, LOAD_TIMEOUT_MS);
  }

  function showUnavailable(root, status) {
    if (!status) return;
    var instagram = root.getAttribute('data-instagram');
    status.textContent = '';

    status.appendChild(
      document.createTextNode(
        'The signup form didn\u2019t load. If you use a content blocker, allow this page and refresh.'
      )
    );

    if (instagram) {
      status.appendChild(document.createTextNode(' You can also follow '));
      var link = document.createElement('a');
      link.href = instagram;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = 'Obsidian on Instagram';
      status.appendChild(link);
      status.appendChild(document.createTextNode(' for the reveal.'));
    }

    if (root.hasAttribute('data-design-mode')) {
      status.appendChild(
        document.createTextNode(
          ' (Editor note: check the form ID, that the form is published as an embed, and that the Klaviyo app embed is on.)'
        )
      );
    }
  }

  function showConfirmedIfReturning(root) {
    if (params.get(CONFIRMED_PARAM) !== '1') return;

    var panel = root.querySelector('[data-ot-signup-panel]');
    var confirmed = root.querySelector('[data-ot-confirmed]');
    if (!confirmed) return;

    if (panel) panel.hidden = true;
    confirmed.hidden = false;
    track('ostrovsky_signup_confirmed', {});

    // Drop the flag so a copied or shared URL doesn't carry it. Campaign
    // params stay.
    try {
      params.delete(CONFIRMED_PARAM);
      var query = params.toString();
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + (query ? '?' + query : '') + window.location.hash
      );
    } catch (e) {
      /* no-op */
    }
  }

  /* ---- "Notify me" jump links -------------------------------------- */

  function focusSignup(root) {
    var confirmed = root.querySelector('[data-ot-confirmed]:not([hidden])');
    var input = root.querySelector(
      '[data-ot-klaviyo] input[type="email"], [data-ot-klaviyo] input[name="email"]'
    );
    var target = confirmed || input || root;
    try {
      target.focus({ preventScroll: true });
    } catch (e) {
      target.focus();
    }
  }

  function initJumps() {
    document.addEventListener('click', function (event) {
      var link = event.target && event.target.closest && event.target.closest('[data-ot-jump]');
      if (!link) return;

      var root = document.getElementById(SIGNUP_ID);
      if (!root) return;

      event.preventDefault();
      root.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
      focusSignup(root);
      track('ostrovsky_cta_click', { cta_location: link.getAttribute('data-ot-jump') || '' });
    });
  }

  /* ---- Background loops -------------------------------------------- */

  function initVideos() {
    [].forEach.call(document.querySelectorAll('[data-ot-video]'), function (wrap) {
      var video = wrap.querySelector('video');
      var toggle = wrap.querySelector('[data-ot-video-toggle]');
      if (!video) return;

      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;

      // Reduced motion: never start on its own; the button still lets the
      // visitor opt in.
      var userPaused = reduced;

      function sync() {
        if (!toggle) return;
        var playing = !video.paused;
        toggle.setAttribute('aria-label', playing ? 'Pause background video' : 'Play background video');
        toggle.classList.toggle('is-paused', !playing);
      }

      function play() {
        var attempt = video.play();
        if (attempt && attempt.catch) attempt.catch(sync);
      }

      video.addEventListener('playing', function () {
        wrap.classList.add('is-playing');
        sync();
      });
      video.addEventListener('pause', sync);

      if (toggle) {
        toggle.hidden = false;
        toggle.addEventListener('click', function () {
          if (video.paused) {
            userPaused = false;
            play();
          } else {
            userPaused = true;
            video.pause();
          }
        });
      }

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(
          function (entries) {
            var visible = entries[0].isIntersecting;
            if (visible && !userPaused) play();
            if (!visible && !video.paused) video.pause();
          },
          { threshold: 0.2 }
        ).observe(wrap);
      } else if (!userPaused) {
        play();
      }

      sync();
    });
  }

  /* ---- Countdown -----------------------------------------------------
     Only ever runs from a merchant-entered timestamp with an explicit
     timezone; anything else and it stays hidden. */

  function initCountdown() {
    var el = document.querySelector('[data-ot-countdown]');
    if (!el) return;

    var iso = el.getAttribute('data-launch') || '';
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(Z|[+-]\d{2}:\d{2})$/.test(iso)) return;
    var target = Date.parse(iso);
    if (isNaN(target)) return;

    function render() {
      var remaining = target - Date.now();
      if (remaining <= 0) {
        el.hidden = true;
        return false;
      }
      var minutes = Math.floor(remaining / 60000);
      var days = Math.floor(minutes / 1440);
      var hours = Math.floor((minutes % 1440) / 60);
      var mins = minutes % 60;
      el.textContent =
        'In ' +
        (days ? days + (days === 1 ? ' day ' : ' days ') : '') +
        hours + 'h ' + (mins < 10 ? '0' : '') + mins + 'm';
      el.hidden = false;
      return true;
    }

    if (render()) {
      var timer = window.setInterval(function () {
        if (!render()) window.clearInterval(timer);
      }, 30000);
    }
  }

  /* ---- Scroll reveal (opt-in motion) -------------------------------- */

  function initReveal() {
    if (reduced || !('IntersectionObserver' in window)) return;
    document.documentElement.classList.add('ot-motion');

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    [].forEach.call(document.querySelectorAll('.ot [data-ot-reveal]'), function (el) {
      observer.observe(el);
    });
  }

  track('ostrovsky_teaser_view', {});
  initSignup();
  initJumps();
  initVideos();
  initCountdown();
  initReveal();
})();
